import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import RefLink from '../shared/RefLink';
import { ChevronRight, ArrowUpDown } from 'lucide-react';
import { fetchJSON } from '../../utils/api';
import { formatDateShort } from '../../utils/dateFormat';
import { PAGE_SIZE } from '../SortableTable';
import EngagementStageBadge, { StageChip } from './EngagementStageBadge';
import NowName from '../shared/NowName';
import { DateCell, LeadsCell } from '../shared/engagementListColumns';
import { engagementTag } from '../../utils/engagementName';
import EngagementStatusFilter from '../shared/EngagementStatusFilter';
import SearchSelect from '../shared/SearchSelect';
import { boardCardLabel, boardFyChip, boardServiceChip } from './constants';

// The Engagement Progress board.
// It lives ONLY as the Grouped view of two
// surfaces — Management → Engagements (scope='manage') and My Records →
// Engagements (scope='mine') — so it sits here and the pages stay thin. One
// card per approved Engagement Registration where there is one, otherwise one
// card per client group + FYE year (year-end dates may differ within a card;
// each row prints its own); expand a card to see the derived pipeline:
// Registration → QC6/QC7 → CW / CC / Team → Signed Financial Statements → QC35.
// (Archival is not a separate stage — the archival date rides on
// the QC35 Form submission, so QC35 Form approval IS the archival.)
// Current stage reads Finished once every stage is done, or whenever the
// status is ticked Finished in Dropdown Lists.
//
// TWO different things sit side by side on purpose:
//   Status        — the engagement master's own field. Human-maintained, and
//                   the entry ticket for Late Accounts / Late Archival.
//   Current stage — DERIVED live from the workflows, read-only. "Where is this
//                   stuck?" — the question the board exists to answer.
//
// Both the stage derivation and the wording come from the server
// (services/engagementStage), the SAME module Overview's My engagements uses.
//
// Props:
//   scope        — 'manage' for the Management surface: the endpoint then
//                  gates on engagements:edit and shows every project (the
//                  list next to it shows everything, so must the board).
//                  'mine' for My Records: always membership-scoped, even for
//                  qm:view / super_admin — My Records means mine.
//   engagementTo — optional (row) => route; each engagement name links there
//                  (Management → /qm/engagements/:id, My → /my/engagements/:id).
//   toolbarExtra — optional node appended to the filter bar (view toggle).
//   showAudit    — false drops the Signed Financial Statements / QC35 Form
//                  gates and Next Deadline (My Records, viewer without an
//                  Audit key — canSeeAuditStages). Default true.
// The five gates in pipeline order — the board's column headers and the
// Client Hub's per-year chips read the same list.
export const STAGES = [
  ['registration', 'Registration'],
  ['qc', 'QC6/QC7'],
  ['cwcc', 'CW / CC / Team'],
  ['fs', 'Signed Financial Statements'],
  ['qc35', 'QC35 Form'],
];
// The gates only the Audit keys see on My Records (showAudit).
const AUDIT_STAGE_KEYS = ['fs', 'qc35'];

// 「下一個提醒日」欄——FS 簽署前＝QC6/QC7 填的預計簽署日、簽署後＝歸檔
// 期限、歸檔完成＝無（判準在後端 utils/archivalDeadline.js，這裡只呈現）。
// 30 天內與已過期都以琥珀標示（未達標用琥珀不用紅），過期另外用字講明。
const DEADLINE_SOON_DAYS = 30;
const DEADLINE_KIND_LABEL = {
  fs_signing: 'Expected FS Signing',
  archival: 'Archival Deadline',
};

// 篩選列：集團（SearchSelect，值＝client_groups.id；沒連到集團的案固定排
// 最後一項）、FYE 年度、合夥人、服務。選項由後端共用收集器
// （server/utils/boardFilterOptions.js）依可見範圍回傳，取自案子實際用到的
// 值；My Records 只看得到自己案子的值。
// Management 的 List 視圖讀同一組網址參數、用同一個 BoardFilterSelects，
// 切換視圖時篩選條件與下拉內容都不變。
export const BOARD_FILTER_KEYS = ['group', 'fy', 'partner', 'director', 'manager', 'service'];
export const UNLINKED_GROUP = 'unlinked';
const filterSelectCls = 'px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
const EMPTY_FILTER_OPTIONS = { groups: [], years: [], partners: [], directors: [], managers: [], services: [] };

// values＝{ group, fy, partner, director, manager, service }；setFilters(patch) 寫回網址。
export const BoardFilterSelects = ({ values, setFilters, filterOptions }) => {
  const fo = filterOptions || EMPTY_FILTER_OPTIONS;
  const { group, fy, partner, director, manager, service } = values;
  const anyFilter = BOARD_FILTER_KEYS.some((k) => values[k]);
  return (
    <>
      <div className="w-full sm:w-72">
        <SearchSelect
          value={group}
          onChange={(v) => setFilters({ group: v })}
          options={[...fo.groups, { value: UNLINKED_GROUP, label: 'Unlinked (Legacy)' }]}
          placeholder="All client groups"
        />
      </div>
      <select value={fy} onChange={(e) => setFilters({ fy: e.target.value })} className={filterSelectCls}>
        <option value="">All FYE years</option>
        {fo.years.map((y) => <option key={y} value={y}>FYE {y}</option>)}
      </select>
      <select value={partner} onChange={(e) => setFilters({ partner: e.target.value })} className={filterSelectCls}>
        <option value="">All partners</option>
        {fo.partners.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      {/* Director／Manager 一案可有多位——選一個人，案上任一位是他就列出。 */}
      <select value={director} onChange={(e) => setFilters({ director: e.target.value })} className={filterSelectCls}>
        <option value="">All directors</option>
        {fo.directors.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      <select value={manager} onChange={(e) => setFilters({ manager: e.target.value })} className={filterSelectCls}>
        <option value="">All managers</option>
        {fo.managers.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      <select value={service} onChange={(e) => setFilters({ service: e.target.value })} className={filterSelectCls}>
        <option value="">All services</option>
        {fo.services.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      {anyFilter && (
        <button
          type="button"
          onClick={() => setFilters(Object.fromEntries(BOARD_FILTER_KEYS.map((k) => [k, ''])))}
          className="text-sm text-blue-600 hover:underline whitespace-nowrap"
        >
          Clear Filters
        </button>
      )}
    </>
  );
};

const DeadlineCell = ({ deadline }) => {
  if (!deadline) return <span className="text-slate-400">—</span>;
  const overdue = deadline.daysLeft < 0;
  const soon = !overdue && deadline.daysLeft <= DEADLINE_SOON_DAYS;
  return (
    <span className="inline-flex flex-col gap-0.5" title={DEADLINE_KIND_LABEL[deadline.kind] || 'Deadline'}>
      <span className={`whitespace-nowrap ${overdue || soon ? 'text-amber-700 font-medium' : 'text-slate-600'}`}>
        {formatDateShort(deadline.date)}
        {overdue && ` · overdue ${Math.abs(deadline.daysLeft)} d`}
        {soon && (deadline.daysLeft === 0 ? ' · due today' : ` · ${deadline.daysLeft} d left`)}
      </span>
      <span className="text-[10px] uppercase tracking-wide text-slate-400 whitespace-nowrap">
        {DEADLINE_KIND_LABEL[deadline.kind] || 'Deadline'}
      </span>
    </span>
  );
};

const ProjectCard = ({ project, engagementTo, showAudit }) => {
  const stages = showAudit ? STAGES : STAGES.filter(([key]) => !AUDIT_STAGE_KEYS.includes(key));
  // 專案卡預設收合,一眼看得到全部專案再自己展開。
  const [open, setOpen] = useState(false);
  const hidden = project.engagementCount - project.engagements.length;
  return (
    <div className="bg-white rounded-lg border border-slate-200">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 rounded-t-lg"
      >
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`} />
          <span className="text-sm font-semibold text-slate-900"><NowName value={project.clientGroup} now={project.clientGroupNow} /></span>
          {/* 卡＝集團＋FYE 年份（各列印自己的 FYE 日期）；服務名列全部不重複的。 */}
          <span className="inline-flex items-center gap-1.5">
            <span className={boardCardLabel}>FYE</span>
            <span className={boardFyChip}>{project.fyeYear || String(project.yearEnd || '').slice(0, 4)}</span>
          </span>
          {(project.services || []).length > 0 && (
            <span className="inline-flex flex-wrap items-center gap-1.5">
              <span className={boardCardLabel}>{project.services.length === 1 ? 'Service' : 'Services'}</span>
              {project.services.map((s) => <span key={s} className={boardServiceChip}>{s}</span>)}
            </span>
          )}
        </span>
        {/* 篩掉幾筆要講出來,否則被藏起來的那些會讓人以為專案只有這幾案。 */}
        <span className="ml-3 shrink-0 text-xs text-slate-500">
          {hidden > 0
            ? `${project.engagements.length} of ${project.engagementCount} engagements`
            : `${project.engagementCount} engagement${project.engagementCount === 1 ? '' : 's'}`}
        </span>
      </button>
      {open && (
        <div className="border-t border-slate-100 table-scroll">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                {/* 公司與 FYE 分兩欄——組合案名「Entity - UEN -
                    FYE…」一長串，掃表時公司名與年度混在一起難讀。 */}
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase min-w-[14rem]">Entity Name</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">FYE</th>
                {/* 同公司同 FYE 可多案（一服務一案、打勾的服務再編號）——沒有這欄兩列長得一樣。 */}
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Service</th>
                {/* 誰帶這個案——三個角色併一欄（List 視圖的 Partner ＋
                    Director / Manager 兩欄，在這張已經很寬的表裡併成一欄）。 */}
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Leads</th>
                {stages.map(([key, label]) => (
                  <th key={key} className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">{label}</th>
                ))}
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Current Stage</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                {showAudit && <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Next Deadline</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {project.engagements.map((e) => (
                <tr key={e.engagementId} className="hover:bg-slate-50">
                  {/* legacy 案（未連結 entity）沒有 entityName——退回組合案名，
                      至少講得出這一列是誰。 */}
                  <td className="px-4 py-2 text-slate-700">
                    {engagementTo ? (
                      <RefLink to={engagementTo(e)} className="text-blue-600 hover:underline">
                        <NowName value={e.entityName || e.engagementName} now={e.entityNameNow} />
                      </RefLink>
                    ) : <NowName value={e.entityName || e.engagementName} now={e.entityNameNow} />}
                    {e.amendmentOpen && (
                      <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-700">
                        Amendment in progress
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    <DateCell value={e.yearEnd || project.yearEnd} />
                  </td>
                  {/* 服務＋序號＋serviceDescription（engagementTag）——同公司同 FYE 的
                      AUP 與 AUP #2 靠這格分開（公司名欄只印公司）。 */}
                  <td className="px-3 py-2 text-sm text-slate-600">
                    {engagementTag(e.serviceProvided, e.engagementSeq, e.serviceDescription) || '—'}
                  </td>
                  <td className="px-3 py-2 text-sm min-w-[12rem] max-w-[18rem]">
                    <LeadsCell r={e} roles={['partner', 'director', 'manager']} />
                  </td>
                  {/* 在審的 chip 點得開「在等誰簽核」——能力在 StageChip /
                      EngagementStageBadge 裡，各畫面共用。 */}
                  {stages.map(([key]) => (
                    <td key={key} className="px-3 py-2">
                      <StageChip state={e.stages[key]} engagementId={e.engagementId} stageKey={key} />
                    </td>
                  ))}
                  <td className="px-3 py-2">
                    <EngagementStageBadge stage={e.currentStage} engagementId={e.engagementId} />
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{e.status}</td>
                  {showAudit && (
                    <td className="px-3 py-2 text-xs">
                      <DeadlineCell deadline={e.nextDeadline} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const EngagementProgressBoard = ({ scope, engagementTo, toolbarExtra = null, showAudit = true }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // null = 還沒收到伺服器的預設值;收到後才由使用者接手。
  const [statuses, setStatuses] = useState(null);
  const [dir, setDir] = useState('desc');   // 預設 FYE 新到舊
  const [page, setPage] = useState(1);
  // BOARD_FILTER_KEYS 的篩選放在網址上，重新整理或分享連結都還在。查詢字串
  // 由同一份鍵組出來，加一個下拉不會漏掉送出這一段。
  const [searchParams, setSearchParams] = useSearchParams();
  const filterValues = Object.fromEntries(BOARD_FILTER_KEYS.map((k) => [k, searchParams.get(k) || '']));
  const filterQuery = BOARD_FILTER_KEYS
    .filter((k) => filterValues[k])
    .map((k) => `${k}=${encodeURIComponent(filterValues[k])}`)
    .join('&');
  const setFilters = (patch) => {
    setPage(1);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
      return next;
    }, { replace: true });
  };

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ dir, page: String(page), pageSize: String(PAGE_SIZE) });
      if (scope) params.set('scope', scope);
      if (statuses !== null) params.set('statuses', statuses.join(','));
      const res = await fetchJSON(`/api/workflow/progress?${params.toString()}${filterQuery ? `&${filterQuery}` : ''}`);
      setData(res);
      if (statuses === null) setStatuses(res.activeStatuses || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [dir, page, statuses, scope, filterQuery]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = (value) => {
    setPage(1);
    setStatuses((prev) => {
      const cur = prev || [];
      return cur.includes(value) ? cur.filter((s) => s !== value) : [...cur, value];
    });
  };

  const options = (data && data.statusOptions) || [];
  const active = statuses || (data && data.activeStatuses) || [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <>
      {/* Filter bar. Status filtering applies per ENGAGEMENT: a card with a Discontinued
          engagement keeps its running ones and reports "2 of 3". */}
      <div className="bg-white rounded-lg border border-slate-200 p-3 mb-4 flex flex-wrap items-center gap-2">
        <EngagementStatusFilter options={options} active={active} onToggle={toggleStatus} />
        <button
          type="button"
          onClick={() => { setPage(1); setDir((d) => (d === 'asc' ? 'desc' : 'asc')); }}
          className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-50"
        >
          <ArrowUpDown className="w-3.5 h-3.5" />
          FYE {dir === 'asc' ? 'Oldest First' : 'Newest First'}
        </button>
        {toolbarExtra}
        <div className="basis-full flex flex-wrap items-center gap-2 pt-2 mt-1 border-t border-slate-100">
          <BoardFilterSelects values={filterValues} setFilters={setFilters} filterOptions={data && data.filterOptions} />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm mb-4">{error}</div>
      )}

      {!data ? (
        <p className="text-center text-sm text-slate-500 mt-8">Loading…</p>
      ) : data.projects.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-500 text-sm">
          {/* 兩個不同的空:一筆都沒有 vs 被狀態篩掉了。講錯會讓人去調篩選找
              一個不存在的案子。 */}
          {data.totalEngagements === 0
            ? 'No engagements yet.'
            : 'No engagement matches the selected filters.'}
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {data.projects.map((p) => (
              <ProjectCard key={p.key} project={p} engagementTo={engagementTo} showAudit={showAudit} />
            ))}
          </div>
          <div className="flex items-center justify-between mt-4 text-sm">
            <span className="text-slate-500">
              {data.total} project{data.total === 1 ? '' : 's'} · page {data.page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={data.page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={data.page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default EngagementProgressBoard;
